import Phaser from 'phaser';
import { t, setLocale, detectLocale } from '@i18n/index';
import { loadMapData } from '../mapData';
import { PALETTE, titleStyle, readoutStyle } from '../theme';
import { queueImages } from '../assets';
import { applyStoredSettings } from '../ui/settingsPanel';

/**
 * Laedt die Kartendaten und legt sie in die Registry, damit spaetere Szenen
 * sie ohne erneutes Dekodieren nutzen koennen. Das Dekodieren des Hoehenrasters
 * dauert je nach Geraet einige hundert Millisekunden — lange genug, dass eine
 * Anzeige noetig ist, zu kurz fuer echte Ladebalken-Dramatik.
 */
export class PreloadScene extends Phaser.Scene {
  constructor() {
    super('Preload');
  }

  create(): void {
    setLocale(detectLocale());

    const { width, height } = this.scale;
    const centreX = width / 2;

    this.add.text(centreX, height / 2 - 40, t('game.title'), titleStyle(34)).setOrigin(0.5);

    const status = this.add
      .text(centreX, height / 2 + 16, t('preload.loading'), readoutStyle(13, true))
      .setOrigin(0.5);

    const barWidth = 260;
    const barX = centreX - barWidth / 2;
    const barY = height / 2 + 44;

    this.add.rectangle(centreX, barY, barWidth, 2, PALETTE.panelEdge);
    const progress = this.add.rectangle(barX, barY, 0, 2, PALETTE.coast).setOrigin(0, 0.5);

    void this.loadEverything(progress, barWidth, status);
  }

  /**
   * Faehrt den Phaser-Lader von Hand.
   *
   * Nicht ueber `preload()`, weil der Lader dann laeuft, bevor irgendetwas
   * gezeichnet ist — man saehe eine Sekunde lang nichts. So teilt er sich den
   * Balken mit den Kartendaten.
   *
   * Ein fehlendes Bild haelt das Spiel nicht auf: Die Szenen fragen vorher, ob
   * eine Textur da ist, und zeichnen sonst ihren Rahmen leer.
   */
  private loadImages(onProgress: (fraction: number) => void): Promise<void> {
    queueImages(this.load);
    if (this.load.list.size === 0) return Promise.resolve();

    return new Promise((resolve) => {
      this.load.on('progress', onProgress);
      this.load.once('complete', () => {
        this.load.off('progress', onProgress);
        resolve();
      });
      this.load.start();
    });
  }

  private async loadEverything(
    progress: Phaser.GameObjects.Rectangle,
    barWidth: number,
    status: Phaser.GameObjects.Text,
  ): Promise<void> {
    try {
      // Erst die Bilder, dann die Karte. Beide teilen sich den Balken, damit
      // er einmal durchlaeuft statt zweimal von vorn zu beginnen.
      await this.loadImages((fraction) => {
        progress.width = barWidth * fraction * 0.45;
      });

      const data = await loadMapData((fraction) => {
        progress.width = barWidth * (0.45 + fraction * 0.55);
      });

      this.registry.set('mapData', data);

      // Gespeicherte Einstellungen haben Vorrang vor den Voreinstellungen —
      // Sprache, Ton und Schriftgroesse.
      applyStoredSettings();

      // Ohne Verzögerung weiter: Die Folgeszene blendet selbst ein, und eine
      // zusätzliche Wartezeit hinge nur vom Szenentakt ab.
      this.scene.start('Menu');
    } catch (error) {
      status.setText(t('preload.failed'));
      status.setColor('#c9736f');
      console.error('Kartendaten konnten nicht geladen werden:', error);
    }
  }
}
